import React, { useEffect, useState, useRef } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { LifeBuoy, Search, Send, ArrowLeft, Circle, Filter } from 'lucide-react';

const STATUS_STYLES = {
  open: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  in_progress: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  resolved: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  closed: 'bg-muted text-muted-foreground border-border',
};

const PRIORITY_STYLES = {
  urgent: 'bg-red-500/15 text-red-600 border-red-500/30',
  high: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
  normal: 'bg-muted text-muted-foreground border-border',
  low: 'bg-muted text-muted-foreground border-border',
};

export const AdminSupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'open', priority: 'all', search: '' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { load(); }, [filters.status, filters.priority]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.priority && filters.priority !== 'all') params.priority = filters.priority;
      if (filters.search) params.search = filters.search;
      const [tRes, sRes] = await Promise.all([
        adminAPI.listSupportTickets(params),
        adminAPI.getSupportStats(),
      ]);
      setTickets(tRes.data);
      setStats(sRes.data);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally { setLoading(false); }
  };

  const openTicket = async (id) => {
    try {
      const res = await adminAPI.getSupportTicket(id);
      setSelectedTicket(res.data.ticket);
      setMessages(res.data.messages);
      load();
    } catch (e) {
      toast.error('Failed to load ticket');
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await adminAPI.replySupportTicket(selectedTicket.id, reply.trim());
      setReply('');
      const res = await adminAPI.getSupportTicket(selectedTicket.id);
      setSelectedTicket(res.data.ticket);
      setMessages(res.data.messages);
      toast.success('Reply sent');
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed to send');
    } finally { setSending(false); }
  };

  const updateStatus = async (data) => {
    try {
      await adminAPI.updateSupportTicket(selectedTicket.id, data);
      const res = await adminAPI.getSupportTicket(selectedTicket.id);
      setSelectedTicket(res.data.ticket);
      toast.success('Updated');
      load();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  if (selectedTicket) {
    return (
      <AdminLayout>
        <div className="space-y-4" data-testid="admin-support-thread">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedTicket(null); setMessages([]); }} data-testid="admin-back-tickets">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to queue
            </Button>
            <div className="flex gap-2 flex-wrap">
              <Select value={selectedTicket.status} onValueChange={(v) => updateStatus({ status: v })}>
                <SelectTrigger className="w-40" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedTicket.priority} onValueChange={(v) => updateStatus({ priority: v })}>
                <SelectTrigger className="w-32" data-testid="admin-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle data-testid="admin-ticket-subject">{selectedTicket.subject}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    From <span className="font-medium">{selectedTicket.user_name}</span> ({selectedTicket.user_email}) · {selectedTicket.category} · Opened {formatDate(selectedTicket.created_at)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className={STATUS_STYLES[selectedTicket.status]}>{selectedTicket.status.replace('_', ' ')}</Badge>
                  <Badge variant="outline" className={PRIORITY_STYLES[selectedTicket.priority]}>{selectedTicket.priority}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      m.sender_role === 'admin'
                        ? 'bg-blue-500/10 border border-blue-500/20'
                        : 'bg-muted'
                    }`} data-testid={`admin-msg-${m.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs font-medium">
                          {m.sender_role === 'admin' ? `${m.sender_name || 'Support'} (admin)` : m.sender_name || m.sender_email}
                        </p>
                        <span className="text-[10px] text-muted-foreground">{formatDate(m.created_at)}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-words">{m.message}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="mt-4 border-t pt-4 space-y-2">
                <Textarea
                  placeholder="Type your reply to the user..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={3}
                  data-testid="admin-reply-input"
                />
                <div className="flex justify-end">
                  <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="admin-send-reply">
                    <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending...' : 'Send Reply'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-support-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Support Tickets</h1>
          <p className="mt-2 text-muted-foreground">Manage and respond to user support requests</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Open', value: stats.open, key: 'open' },
            { label: 'In Progress', value: stats.in_progress, key: 'in_progress' },
            { label: 'Resolved', value: stats.resolved, key: 'resolved' },
            { label: 'Closed', value: stats.closed, key: 'closed' },
            { label: 'Unread', value: stats.unread, key: 'unread' },
          ].map(s => (
            <Card key={s.key} data-testid={`stat-${s.key}`}>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</p>
                <p className="text-2xl font-semibold mt-1">{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5" /> Ticket Queue</CardTitle>
              <div className="flex gap-2 flex-wrap items-center">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
                  <SelectTrigger className="w-32" data-testid="filter-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All priorities</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-9 w-52"
                    placeholder="Search..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && load()}
                    data-testid="filter-search"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading...</p>
            ) : tickets.length === 0 ? (
              <p className="text-center py-12 text-muted-foreground">No tickets match the current filters.</p>
            ) : (
              <div className="space-y-2">
                {tickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => openTicket(t.id)}
                    className="w-full text-left p-4 rounded-lg border border-border hover:bg-muted/60 transition-colors"
                    data-testid={`admin-ticket-row-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {t.unread_by_admin && <Circle className="h-2 w-2 fill-blue-500 text-blue-500 shrink-0" />}
                          <p className="font-medium truncate">{t.subject}</p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t.user_name} ({t.user_email}) · {t.category} · {t.message_count} msg · Updated {formatDate(t.updated_at)}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Badge variant="outline" className={STATUS_STYLES[t.status]}>{t.status.replace('_', ' ')}</Badge>
                        <Badge variant="outline" className={PRIORITY_STYLES[t.priority]}>{t.priority}</Badge>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
